import React, { useState } from 'react';

// Styles & Assets
import styles from './CalendarEvent.module.css';
import { ReactComponent as BinIcon } from '../../../assets/icons/icon-bin.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/icon-edit.svg';
import { ReactComponent as ExitIcon } from '../../../assets/icons/icono-salir.svg';

// Hooks
import { useTranslation } from 'react-i18next';

// Components
import Modal from '../../UI/Modal/Modal';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import { getFrequencies, getFrequencyPoll } from '../../../services/getPollAttribute';
import Select from '../../UI/Select/Select';
import FrequencySelector from '../../UI/FrequencySelector/FrequencySelector';
import SelectorSimple from '../../Helpers/SelectorSimple/SelectorSimple';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Other
import moment from 'moment';
import HourSelector from '../../UI/HourSelector/HourSelector';
import { HH, mm } from '../../../interfaces/Time';

// Interfaces 
import { ICalendarEvent, IDateEventType } from '../../../interfaces/Calendar';

const DEFAULT_EVENT: ICalendarEvent = {
    id: 0,
    patient_id: 48,
    event_type_id: 1,
    date_time: new Date('05-07-2024 10:00'),
    end_date_time: new Date('05-07-2024 10:00'),
    previous_time_reminder: 30,
    description: 'Consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam ',
    frecuency: 7,
    frequency_duration: null,
    semanal_frequency: null,
    monthly_frequency_day: null,
};

// Interfaces
interface Props {
    viewMode?: boolean
}

const CalendarEvent: React.FC<Props> = ({ viewMode }) => {

    const { t } = useTranslation();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showFrequencySelector, setShowFrequencySelector] = useState(false);

    const [isEditing, setIsEditing] = useState(false);

    const [event, setEvent] = useState<ICalendarEvent>(DEFAULT_EVENT);

    const handleChange = () => {
        //
    };

    const handleChangeFreq = () => {
        //
    };

    const eventTypes: IDateEventType[] = [{ id: 0, name: 'Visita Presencial Hospital' },
    { id: 1, name: 'Seguimiento Telefónico' }, { id: 2, name: 'Visita Presencial Domicilio' }
        , { id: 3, name: 'Visita on-line' }];

    const OPTIONS_EVENT_TYPE = eventTypes.map((n, i) => ({ id: i, label: <p className={styles.optionSelector}>{n.name}</p> }));

    const SELECT_OPTIONS = getFrequencies().map(id => {
        return { id, element: <div className={styles.optionSelector}>{t(id)}</div> };
    });

    const defaultDate = moment(event.date_time, 'DD-MM-YYYY HH:mm');

    const newTheme = createTheme({
        components: {
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        fontSize: '15px',
                        padding: '12px 0 12px 12px !important',
                        fontFamily: 'MontserratRegular'
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: { margin: '0' }
                }
            }
        }
    });

    return (
        <div className='dialog'>
            <div className={styles.dialogHeader}>
                <h1 className='dialogTitle'>{isEditing ? t('edit_appointment') : t('appointment')}</h1>
                {!isEditing && !viewMode &&
                    <div className={styles.rightButtons}>
                        <button className={styles.roundButton} onClick={() => setShowDeleteDialog(true)}>
                            <BinIcon />
                        </button>
                        <Modal show={showDeleteDialog} setShowModal={setShowDeleteDialog} secondModal>
                            <div className='dialog'>
                                <h1 className='dialogSubTitle'>
                                    ¿Estás seguro/a de que quieres eliminar el evento? Esta acción no puede deshacerse.
                                </h1>
                                <CancelConfirmButtons
                                    onCancel={() => setShowDeleteDialog(false)}
                                    onConfirm={() => {/* */ }}
                                    customStyles={{ marginTop: '2rem' }}
                                    customButtonStyles={{ lineHeight: '2rem' }}
                                />
                            </div>
                        </Modal>
                        <button className={styles.roundButton} onClick={() => setIsEditing(true)}>
                            <EditIcon style={{ marginLeft: '2px' }} />
                        </button>
                        {event.event_type_id === 1 &&
                            <button className={styles.filledButton}>
                                <p>{t('join')}</p>
                                <ExitIcon />
                            </button>
                        }
                    </div>
                }
            </div>
            <div className={styles.content}>
                <div className={styles.sideContent}>
                    <h3 className={styles.categoryTitle}>{t('patient')}</h3>
                    {isEditing ?
                        <Select
                            selector={(props) => <SelectorSimple
                                {...props} value={`${event.patient_id} - Pepa Pérez Calabuig`}
                            />}
                            options={[]}
                            onSelect={() => {/* */ }}
                        />
                        :
                        <p className={styles.categoryText}>{event.patient_id} - Pepa Pérez Calabuig</p>
                    }
                    <h3 className={styles.categoryTitle}>{t('type_appointment')}</h3>
                    {isEditing ?
                        <Select
                            selector={(props) => <SelectorSimple {...props} />}
                            options={OPTIONS_EVENT_TYPE}
                            onSelect={() => {/* */ }}
                        />
                        :
                        <p className={styles.categoryText}>{event.event_type_id}</p>
                    }
                    <h3 className={styles.categoryTitle}>{t('date')}</h3>
                    {isEditing ?
                        <div className={styles.dateContainer}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <ThemeProvider theme={newTheme}>
                                    <DatePicker className={styles.datePicker} defaultValue={defaultDate} format='DD-MM-YYYY' />
                                </ThemeProvider>
                            </LocalizationProvider>
                            <HourSelector
                                hours={defaultDate.hour().toString() as HH}
                                minutes={defaultDate.minute().toString() as mm}
                                handleChange={() => {/* */ }}
                                style={{ border: '1px solid #acacac', borderRadius: '5px', padding: '12px 17px', justifyContent: 'space-between', color: '#777' }}
                            />
                        </div>
                        :
                        <p className={styles.categoryText}>{event.date_time.toISOString()}</p>
                    }
                    <h3 className={styles.categoryTitle}>{t('periodicity')}</h3>
                    {isEditing ?
                        <>
                            <Select
                                selector={(props) => <SelectorSimple {...props} value={t(getFrequencyPoll(event))} />}
                                options={SELECT_OPTIONS}
                                onSelect={handleChange}
                            />
                            <Modal
                                show={showFrequencySelector}
                                setShowModal={setShowFrequencySelector}
                            >
                                <FrequencySelector
                                    frequency={{
                                        frecuency: event.frecuency,
                                        monthly_frequency_day: event.monthly_frequency_day,
                                        semanal_frequency: event.semanal_frequency,
                                        frequency_duration: event.frequency_duration,
                                    }}
                                    onCancel={() => setShowFrequencySelector(false)}
                                    onConfirm={handleChangeFreq}
                                    setShow={setShowFrequencySelector}
                                />
                            </Modal>
                        </>
                        :
                        <p className={styles.categoryText}>{t(getFrequencyPoll(event))}</p>
                    }
                    <h3 className={styles.categoryTitle}>{t('reminder')}</h3>
                    {isEditing ?
                        <Select
                            selector={(props) => <SelectorSimple {...props} value={`${event.previous_time_reminder} ${t('minutes_before')}`} />}
                            options={[]}
                            onSelect={() => {/* */ }}
                        />
                        :
                        <p className={styles.categoryText}>{event.previous_time_reminder} {t('minutes_before')}</p>
                    }
                </div>
                <div className={styles.sideContent}>
                    <h3 className={styles.categoryTitle}>{t('description_appointment')}</h3>
                    {isEditing ?
                        <div className={styles.textAreaContainer}>
                            <textarea className={styles.textArea} defaultValue={event.description} name='text' />
                        </div>
                        :
                        <p className={styles.areaDescription}>{event.description}</p>
                    }
                </div>
            </div>
            <div className={styles.closeContainer}>
                {isEditing ?
                    <CancelConfirmButtons
                        onCancel={() => setIsEditing(false)}
                        onConfirm={() => {/* */ }}
                        customStyles={{ marginTop: '1rem' }}
                        customButtonStyles={{ lineHeight: '2rem' }}
                    />
                    :
                    <button className={styles.filledButton}>
                        <p>{t('close')}</p>
                    </button>
                }
            </div>
        </div>
    );
};

export default CalendarEvent;